'use client'

import { useState, Fragment } from 'react'
import Link from 'next/link'
import { Check, X, Sparkles, Zap, Building2, ArrowRight } from 'lucide-react'
import { PublicNav } from '../components/public-nav'
import { PublicFooter } from '../components/public-footer'
import { lp } from '../_home/locale-path'

type Lang = 'de' | 'en' | 'tr'

interface Plan {
  id: 'starter' | 'pro' | 'enterprise'
  name: string
  tagline: string
  monthly: number
  badge?: string
  icon: typeof Sparkles
  highlights: string[]
  recommended?: boolean
}

interface AddOn {
  id: string
  name: string
  price: string
  unit: string
  description: string
}

interface CompareRow {
  name: string
  s: string | boolean
  p: string | boolean
  e: string | boolean
}

interface CompareGroup {
  section: string
  features: CompareRow[]
}

interface Faq {
  q: string
  a: string
}

interface Dict {
  plans: Plan[]
  addons: AddOn[]
  compare: CompareGroup[]
  faqs: Faq[]
  heroEyebrow: string
  heroTitleLine1: string
  heroTitleLine2: string
  heroSubtitle: string
  billingMonthly: string
  billingYearly: string
  perMonth: string
  yearlyNote: (yearly: number, saved: number) => string
  monthlyNote: string
  ctaStartTrial: string
  addonsEyebrow: string
  addonsTitlePre: string
  addonsTitleEm: string
  addonsTitlePost: string
  addonsLead: string
  compareEyebrow: string
  compareTitle: string
  compareColStarter: string
  compareColPro: string
  compareColEnterprise: string
  faqEyebrow: string
  faqTitle: string
  finalTitle: string
  finalSubtitle: string
  finalCtaTrial: string
  finalCtaDemo: string
}

const T: Record<Lang, Dict> = {
  de: {
    plans: [
      {
        id: 'starter',
        name: 'Starter',
        tagline: 'Für den ruhigen Anfang',
        monthly: 29,
        icon: Sparkles,
        highlights: [
          'Bis zu 100 Reservierungen / Monat',
          'Reservierungs-Widget für Ihre Website',
          'Digitale Speisekarte mit QR-Code',
          'E-Mail-Bestätigung an Gäste',
          'Tischplan bis 10 Tische',
          'E-Mail-Support',
        ],
      },
      {
        id: 'pro',
        name: 'Pro',
        tagline: 'Für den vollen Betrieb',
        monthly: 69,
        badge: 'Beliebtester',
        recommended: true,
        icon: Zap,
        highlights: [
          'Unbegrenzte Reservierungen',
          'SMS-Erinnerungen inklusive',
          'Anzahlung gegen No-Show',
          'Online-Bestellungen (0 % Provision)',
          'Personalplanung + Stundennachweis-PDF',
          'Gastbewertungs-Steuerung',
          'Eigene Domain (auf Anfrage)',
          'Telefon-Support (< 2 h)',
        ],
      },
      {
        id: 'enterprise',
        name: 'Premium',
        tagline: 'Für mehrere Häuser',
        monthly: 119,
        icon: Building2,
        highlights: [
          'Alles aus Pro',
          'Bis zu 5 Standorte',
          'Webhooks für eigene Integrationen',
          'Single Sign-On (auf Anfrage)',
          'Persönlicher Ansprechpartner',
          'SLA: 99,9 % Verfügbarkeit',
        ],
      },
    ],
    addons: [
      {
        id: 'sms',
        name: 'SMS-Paket',
        price: '19',
        unit: '€ / Monat',
        description: '500 zusätzliche SMS-Erinnerungen pro Monat. Buchbar ab dem Starter-Plan.',
      },
      {
        id: 'standort',
        name: 'Weiterer Standort',
        price: '39',
        unit: '€ / Standort / Monat',
        description: 'Jeder weitere Standort mit eigenem Tischplan, Personal und Reservierungen. Buchbar ab Pro.',
      },
      {
        id: 'onboarding',
        name: 'Persönliches Onboarding',
        price: '99',
        unit: '€ einmalig',
        description: 'Wir richten Tische, Speisekarte und Widget gemeinsam mit Ihnen ein. 90 Min. Video-Call.',
      },
      {
        id: 'datenmigration',
        name: 'Datenmigration',
        price: '149',
        unit: '€ einmalig',
        description: 'Wir übernehmen den CSV-Export Ihrer Gäste- und Reservierungsdaten von Ihrem bisherigen Anbieter und importieren sie sauber in Rezaro.',
      },
    ],
    compare: [
      { section: 'Reservierungen', features: [
        { name: 'Online-Reservierungen', s: '100/Monat', p: 'Unbegrenzt', e: 'Unbegrenzt' },
        { name: 'Reservierungs-Widget', s: true, p: true, e: true },
        { name: 'Automatische Erinnerungen (E-Mail)', s: true, p: true, e: true },
        { name: 'SMS-Erinnerungen', s: false, p: true, e: true },
        { name: 'Smart-Confirm (Auto/Manuell/X Pers)', s: false, p: true, e: true },
        { name: 'Sicherheitscode-Verifizierung', s: false, p: true, e: true },
        { name: 'Wartelisten-Management', s: false, p: true, e: true },
      ]},
      { section: 'Bestellungen', features: [
        { name: 'Lieferung & Abholung', s: false, p: true, e: true },
        { name: 'QR-Code-Bestellung am Tisch', s: false, p: true, e: true },
        { name: 'Provision pro Bestellung', s: '—', p: '0 %', e: '0 %' },
        { name: 'Stripe-Zahlungsabwicklung', s: false, p: true, e: true },
      ]},
      { section: 'Marketing & Gäste', features: [
        { name: 'Gäste-Datenbank', s: true, p: true, e: true },
        { name: 'Gastbewertungs-Manager', s: false, p: true, e: true },
        { name: 'E-Mail-Kampagnen', s: '200/Monat', p: 'Unbegrenzt', e: 'Unbegrenzt' },
        { name: 'SMS-Kampagnen', s: false, p: true, e: true },
        { name: 'Eigene Domain (auf Anfrage)', s: false, p: true, e: true },
      ]},
      { section: 'Operatives', features: [
        { name: 'Tischplan & Räume', s: '5 Tische', p: 'Unbegrenzt', e: 'Unbegrenzt' },
        { name: 'Schichtplanung', s: false, p: true, e: true },
        { name: 'Anzahlungen', s: false, p: true, e: true },
        { name: 'Mehrere Standorte', s: false, p: false, e: true },
        { name: 'Webhooks für Kassensystem-Anbindung', s: false, p: false, e: true },
        { name: 'Webhooks & REST API', s: false, p: false, e: true },
      ]},
      { section: 'Support', features: [
        { name: 'E-Mail-Support', s: true, p: true, e: true },
        { name: 'Antwortzeit', s: '48 h', p: '< 2 h', e: '< 30 min' },
        { name: 'Onboarding-Call', s: false, p: '60 min', e: 'Unbegrenzt' },
        { name: 'Dedizierter Account-Manager', s: false, p: false, e: true },
        { name: 'SLA-Garantie', s: false, p: false, e: '99,9 %' },
      ]},
    ],
    faqs: [
      { q: 'Brauche ich eine Kreditkarte für den Test?', a: 'Ja. Wir hinterlegen Ihre Karte bei der Registrierung. Nach Ablauf der 7 Tage wird der gewählte Plan automatisch abgebucht. Innerhalb der Testphase jederzeit kostenfrei kündbar — wir buchen dann nichts ab.' },
      { q: 'Kann ich den Plan später wechseln?', a: 'Ja, jederzeit. Upgrade ist sofort aktiv, Downgrade zum Monatsende. Keine Strafgebühren.' },
      { q: 'Gibt es eine Mindestlaufzeit?', a: 'Nein. Sie zahlen monatlich und können jederzeit zum Monatsende kündigen.' },
      { q: 'Werden Provisionen pro Reservierung erhoben?', a: 'Niemals. Sie zahlen einen festen Monatspreis und behalten 100 % jeder Reservierung und Bestellung.' },
      { q: 'Was passiert nach den 7 Tagen?', a: 'Ihre Karte wird automatisch mit dem gewählten Plan belastet (Starter €29, Pro €69 oder Premium €119 pro Monat). Wir senden 24 h vorher eine Erinnerung per E-Mail. Sie können jederzeit kündigen, dann endet die Mitgliedschaft zum Monatsende.' },
      { q: 'Wo werden meine Daten gespeichert?', a: 'Auf Servern in Frankfurt am Main, Deutschland. ISO-27001-zertifiziert, DSGVO-konform.' },
      { q: 'Wie kann ich bezahlen?', a: 'Kreditkarte, SEPA-Lastschrift oder auf Rechnung (ab Pro-Plan).' },
      { q: 'Kann ich vor dem Wechsel meine Daten exportieren?', a: 'Ja, jederzeit. Reservierungen, Gäste und Speisekarte als CSV oder JSON.' },
    ],
    heroEyebrow: '— Preise',
    heroTitleLine1: 'Eine Plattform.',
    heroTitleLine2: 'Ein fairer Preis.',
    heroSubtitle: 'Keine Provisionen pro Reservierung. Keine Vertragsbindung. Keine Überraschungen auf der Rechnung.',
    billingMonthly: 'Monatlich',
    billingYearly: 'Jährlich',
    perMonth: '/ Monat',
    yearlyNote: (yearly, saved) => `Jährlich ${yearly} € · Sie sparen ${saved} €`,
    monthlyNote: 'Monatlich kündbar · 7 Tage testen',
    ctaStartTrial: '7-Tage-Test starten',
    addonsEyebrow: '— Zusatzpakete',
    addonsTitlePre: 'Was Sie nach Bedarf ',
    addonsTitleEm: 'dazubuchen',
    addonsTitlePost: ' können.',
    addonsLead: 'Buchbar im Inhaber-Panel, jederzeit aktivierbar und kündbar. Wird auf der nächsten Rechnung berücksichtigt.',
    compareEyebrow: '— Im Detail',
    compareTitle: 'Was ist im Plan?',
    compareColStarter: 'Starter',
    compareColPro: 'Pro',
    compareColEnterprise: 'Enterprise',
    faqEyebrow: '— FAQ',
    faqTitle: 'Häufige Fragen.',
    finalTitle: 'Noch unsicher?',
    finalSubtitle: 'Vereinbaren Sie ein 20-Minuten-Beratungsgespräch. Wir zeigen Ihnen Rezaro live an Ihrem Restaurant.',
    finalCtaTrial: 'Jetzt kostenlos testen',
    finalCtaDemo: 'Demo vereinbaren',
  },
  en: {
    plans: [
      {
        id: 'starter',
        name: 'Starter',
        tagline: 'For a calm start',
        monthly: 29,
        icon: Sparkles,
        highlights: [
          'Up to 100 reservations / month',
          'Reservation widget for your website',
          'Digital menu with QR code',
          'Email confirmation for guests',
          'Table plan up to 10 tables',
          'Email support',
        ],
      },
      {
        id: 'pro',
        name: 'Pro',
        tagline: 'For full operations',
        monthly: 69,
        badge: 'Most popular',
        recommended: true,
        icon: Zap,
        highlights: [
          'Unlimited reservations',
          'SMS reminders included',
          'Deposit against no-shows',
          'Online orders (0 % commission)',
          'Staff scheduling + hours-report PDF',
          'Guest review management',
          'Custom domain',
          'Phone support (< 2 h)',
        ],
      },
      {
        id: 'enterprise',
        name: 'Premium',
        tagline: 'For multiple venues',
        monthly: 119,
        icon: Building2,
        highlights: [
          'Everything in Pro',
          'Up to 5 locations',
          'Webhooks for your own integrations',
          'Single sign-on (on request)',
          'Personal point of contact',
          'SLA: 99.9 % uptime',
        ],
      },
    ],
    addons: [
      {
        id: 'sms',
        name: 'SMS package',
        price: '19',
        unit: '€ / month',
        description: '500 additional SMS reminders per month. Available from the Starter plan.',
      },
      {
        id: 'standort',
        name: 'Additional location',
        price: '39',
        unit: '€ / location / month',
        description: 'Each additional location with its own table plan, staff and reservations. Available from Pro.',
      },
      {
        id: 'onboarding',
        name: 'Personal onboarding',
        price: '99',
        unit: '€ one-time',
        description: 'We set up tables, menu and widget together with you. 90 min video call.',
      },
      {
        id: 'datenmigration',
        name: 'Data migration',
        price: '149',
        unit: '€ one-time',
        description: 'We take the CSV export of your guest and reservation data from your previous provider and import it cleanly into Rezaro.',
      },
    ],
    compare: [
      { section: 'Reservations', features: [
        { name: 'Online reservations', s: '100/month', p: 'Unlimited', e: 'Unlimited' },
        { name: 'Reservation widget', s: true, p: true, e: true },
        { name: 'Automatic reminders (email)', s: true, p: true, e: true },
        { name: 'SMS reminders', s: false, p: true, e: true },
        { name: 'Smart confirm (auto/manual/X guests)', s: false, p: true, e: true },
        { name: 'Security-code verification', s: false, p: true, e: true },
        { name: 'Waitlist management', s: false, p: true, e: true },
      ]},
      { section: 'Orders', features: [
        { name: 'Delivery & pickup', s: false, p: true, e: true },
        { name: 'QR-code ordering at the table', s: false, p: true, e: true },
        { name: 'Commission per order', s: '—', p: '0 %', e: '0 %' },
        { name: 'Stripe payment processing', s: false, p: true, e: true },
      ]},
      { section: 'Marketing & guests', features: [
        { name: 'Guest database', s: true, p: true, e: true },
        { name: 'Guest review manager', s: false, p: true, e: true },
        { name: 'Email campaigns', s: '200/month', p: 'Unlimited', e: 'Unlimited' },
        { name: 'SMS campaigns', s: false, p: true, e: true },
        { name: 'Custom domain', s: false, p: true, e: true },
      ]},
      { section: 'Operations', features: [
        { name: 'Table plan & rooms', s: '5 tables', p: 'Unlimited', e: 'Unlimited' },
        { name: 'Shift planning', s: false, p: true, e: true },
        { name: 'Deposits', s: false, p: true, e: true },
        { name: 'Multiple locations', s: false, p: false, e: true },
        { name: 'Webhooks for POS integration', s: false, p: false, e: true },
        { name: 'Webhooks & REST API', s: false, p: false, e: true },
      ]},
      { section: 'Support', features: [
        { name: 'Email support', s: true, p: true, e: true },
        { name: 'Response time', s: '48 h', p: '< 2 h', e: '< 30 min' },
        { name: 'Onboarding call', s: false, p: '60 min', e: 'Unlimited' },
        { name: 'Dedicated account manager', s: false, p: false, e: true },
        { name: 'SLA guarantee', s: false, p: false, e: '99.9 %' },
      ]},
    ],
    faqs: [
      { q: 'Do I need a credit card for the trial?', a: 'Yes. We store your card during sign-up. After the 7 days end, the chosen plan is charged automatically. You can cancel free of charge at any time during the trial — then we charge nothing.' },
      { q: 'Can I change my plan later?', a: 'Yes, at any time. An upgrade takes effect immediately, a downgrade at the end of the month. No penalty fees.' },
      { q: 'Is there a minimum contract term?', a: 'No. You pay monthly and can cancel at any time, effective at the end of the month.' },
      { q: 'Are commissions charged per reservation?', a: 'Never. You pay a fixed monthly price and keep 100 % of every reservation and order.' },
      { q: 'What happens after the 7 days?', a: 'Your card is charged automatically for the chosen plan (Starter €29, Pro €69 or Premium €119 per month). We send a reminder by email 24 h beforehand. You can cancel at any time, and the membership then ends at the end of the month.' },
      { q: 'Where is my data stored?', a: 'On servers in Frankfurt am Main, Germany. ISO 27001 certified, GDPR compliant.' },
      { q: 'How can I pay?', a: 'Credit card, SEPA direct debit or by invoice (from the Pro plan).' },
      { q: 'Can I export my data before switching?', a: 'Yes, at any time. Reservations, guests and menu as CSV or JSON.' },
    ],
    heroEyebrow: '— Pricing',
    heroTitleLine1: 'One platform.',
    heroTitleLine2: 'One fair price.',
    heroSubtitle: 'No commission per reservation. No contract lock-in. No surprises on your invoice.',
    billingMonthly: 'Monthly',
    billingYearly: 'Yearly',
    perMonth: '/ month',
    yearlyNote: (yearly, saved) => `${yearly} € per year · You save ${saved} €`,
    monthlyNote: 'Cancel monthly · 7-day trial',
    ctaStartTrial: 'Start 7-day trial',
    addonsEyebrow: '— Add-ons',
    addonsTitlePre: 'What you can ',
    addonsTitleEm: 'add on',
    addonsTitlePost: ' as needed.',
    addonsLead: 'Bookable in the owner panel, activatable and cancellable at any time. Reflected on your next invoice.',
    compareEyebrow: '— In detail',
    compareTitle: "What's in the plan?",
    compareColStarter: 'Starter',
    compareColPro: 'Pro',
    compareColEnterprise: 'Enterprise',
    faqEyebrow: '— FAQ',
    faqTitle: 'Frequent questions.',
    finalTitle: 'Still unsure?',
    finalSubtitle: 'Book a 20-minute consultation. We show you Rezaro live for your restaurant.',
    finalCtaTrial: 'Try it free now',
    finalCtaDemo: 'Book a demo',
  },
  tr: {
    plans: [
      {
        id: 'starter',
        name: 'Starter',
        tagline: 'Sakin bir başlangıç için',
        monthly: 29,
        icon: Sparkles,
        highlights: [
          'Ayda 100 rezervasyona kadar',
          'Web siteniz için rezervasyon widget’ı',
          'QR kodlu dijital menü',
          'Misafirlere e-posta onayı',
          '10 masaya kadar masa planı',
          'E-posta desteği',
        ],
      },
      {
        id: 'pro',
        name: 'Pro',
        tagline: 'Tam işletme için',
        monthly: 69,
        badge: 'En popüler',
        recommended: true,
        icon: Zap,
        highlights: [
          'Sınırsız rezervasyon',
          'SMS hatırlatmaları dahil',
          'No-show’a karşı kapora',
          'Online siparişler (%0 komisyon)',
          'Personel planlama + çalışma saati PDF’i',
          'Misafir yorumu yönetimi',
          'Kendi alan adınız',
          'Telefon desteği (< 2 sa)',
        ],
      },
      {
        id: 'enterprise',
        name: 'Premium',
        tagline: 'Birden fazla şube için',
        monthly: 119,
        icon: Building2,
        highlights: [
          'Pro’daki her şey',
          '5 lokasyona kadar',
          'Kendi entegrasyonlarınız için webhook’lar',
          'Tek oturum açma (talep üzerine)',
          'Kişisel iletişim sorumlusu',
          'SLA: %99,9 erişilebilirlik',
        ],
      },
    ],
    addons: [
      {
        id: 'sms',
        name: 'SMS paketi',
        price: '19',
        unit: '€ / ay',
        description: 'Ayda 500 ek SMS hatırlatması. Starter planından itibaren eklenebilir.',
      },
      {
        id: 'standort',
        name: 'Ek lokasyon',
        price: '39',
        unit: '€ / lokasyon / ay',
        description: 'Her ek lokasyon kendi masa planı, personeli ve rezervasyonlarıyla. Pro’dan itibaren eklenebilir.',
      },
      {
        id: 'onboarding',
        name: 'Kişisel kurulum',
        price: '99',
        unit: '€ tek seferlik',
        description: 'Masaları, menüyü ve widget’ı sizinle birlikte kuruyoruz. 90 dakikalık görüntülü görüşme.',
      },
      {
        id: 'datenmigration',
        name: 'Veri taşıma',
        price: '149',
        unit: '€ tek seferlik',
        description: 'Önceki sağlayıcınızdaki misafir ve rezervasyon verilerinizin CSV dışa aktarımını alır ve Rezaro’ya düzgünce aktarırız.',
      },
    ],
    compare: [
      { section: 'Rezervasyonlar', features: [
        { name: 'Online rezervasyonlar', s: '100/ay', p: 'Sınırsız', e: 'Sınırsız' },
        { name: 'Rezervasyon widget’ı', s: true, p: true, e: true },
        { name: 'Otomatik hatırlatmalar (e-posta)', s: true, p: true, e: true },
        { name: 'SMS hatırlatmaları', s: false, p: true, e: true },
        { name: 'Akıllı onay (oto/manuel/X kişi)', s: false, p: true, e: true },
        { name: 'Güvenlik kodu doğrulaması', s: false, p: true, e: true },
        { name: 'Bekleme listesi yönetimi', s: false, p: true, e: true },
      ]},
      { section: 'Siparişler', features: [
        { name: 'Teslimat & gel-al', s: false, p: true, e: true },
        { name: 'Masada QR kod ile sipariş', s: false, p: true, e: true },
        { name: 'Sipariş başına komisyon', s: '—', p: '%0', e: '%0' },
        { name: 'Stripe ödeme işleme', s: false, p: true, e: true },
      ]},
      { section: 'Pazarlama & misafirler', features: [
        { name: 'Misafir veritabanı', s: true, p: true, e: true },
        { name: 'Misafir yorumu yöneticisi', s: false, p: true, e: true },
        { name: 'E-posta kampanyaları', s: '200/ay', p: 'Sınırsız', e: 'Sınırsız' },
        { name: 'SMS kampanyaları', s: false, p: true, e: true },
        { name: 'Kendi alan adı', s: false, p: true, e: true },
      ]},
      { section: 'Operasyon', features: [
        { name: 'Masa planı & salonlar', s: '5 masa', p: 'Sınırsız', e: 'Sınırsız' },
        { name: 'Vardiya planlama', s: false, p: true, e: true },
        { name: 'Kaporalar', s: false, p: true, e: true },
        { name: 'Birden fazla lokasyon', s: false, p: false, e: true },
        { name: 'POS entegrasyonu için webhook’lar', s: false, p: false, e: true },
        { name: 'Webhook’lar & REST API', s: false, p: false, e: true },
      ]},
      { section: 'Destek', features: [
        { name: 'E-posta desteği', s: true, p: true, e: true },
        { name: 'Yanıt süresi', s: '48 sa', p: '< 2 sa', e: '< 30 dk' },
        { name: 'Kurulum görüşmesi', s: false, p: '60 dk', e: 'Sınırsız' },
        { name: 'Özel hesap yöneticisi', s: false, p: false, e: true },
        { name: 'SLA garantisi', s: false, p: false, e: '%99,9' },
      ]},
    ],
    faqs: [
      { q: 'Deneme için kredi kartı gerekli mi?', a: 'Evet. Kaydolurken kartınızı kaydediyoruz. 7 günün sonunda seçtiğiniz plan otomatik olarak tahsil edilir. Deneme süresi içinde dilediğiniz zaman ücretsiz iptal edebilirsiniz — o durumda hiçbir tahsilat yapmayız.' },
      { q: 'Planımı sonradan değiştirebilir miyim?', a: 'Evet, istediğiniz zaman. Yükseltme hemen geçerli olur, düşürme ay sonunda. Ceza ücreti yoktur.' },
      { q: 'Asgari sözleşme süresi var mı?', a: 'Hayır. Aylık ödersiniz ve dilediğiniz zaman ay sonunda iptal edebilirsiniz.' },
      { q: 'Rezervasyon başına komisyon alınıyor mu?', a: 'Asla. Sabit bir aylık ücret ödersiniz ve her rezervasyon ile siparişin %100’ünü siz alırsınız.' },
      { q: '7 günün sonunda ne olur?', a: 'Kartınız seçtiğiniz plan için otomatik olarak tahsil edilir (Starter €29, Pro €69 veya Premium €119 aylık). 24 saat önce e-posta ile hatırlatma göndeririz. Dilediğiniz zaman iptal edebilirsiniz, üyelik o zaman ay sonunda sona erer.' },
      { q: 'Verilerim nerede saklanıyor?', a: 'Almanya, Frankfurt am Main’deki sunucularda. ISO 27001 sertifikalı, GDPR uyumlu.' },
      { q: 'Nasıl ödeme yapabilirim?', a: 'Kredi kartı, SEPA otomatik ödeme veya faturayla (Pro plandan itibaren).' },
      { q: 'Geçiş yapmadan önce verilerimi dışa aktarabilir miyim?', a: 'Evet, istediğiniz zaman. Rezervasyonlar, misafirler ve menü CSV veya JSON olarak.' },
    ],
    heroEyebrow: '— Fiyatlar',
    heroTitleLine1: 'Tek platform.',
    heroTitleLine2: 'Tek adil fiyat.',
    heroSubtitle: 'Rezervasyon başına komisyon yok. Sözleşme bağı yok. Faturada sürpriz yok.',
    billingMonthly: 'Aylık',
    billingYearly: 'Yıllık',
    perMonth: '/ ay',
    yearlyNote: (yearly, saved) => `Yıllık ${yearly} € · ${saved} € tasarruf edersiniz`,
    monthlyNote: 'Aylık iptal edilebilir · 7 gün deneyin',
    ctaStartTrial: '7 günlük denemeyi başlat',
    addonsEyebrow: '— Ek paketler',
    addonsTitlePre: 'İhtiyaca göre neleri ',
    addonsTitleEm: 'ekleyebilirsiniz',
    addonsTitlePost: '.',
    addonsLead: 'İşletme panelinden eklenebilir, istediğiniz zaman etkinleştirilebilir ve iptal edilebilir. Bir sonraki faturaya yansıtılır.',
    compareEyebrow: '— Ayrıntılı',
    compareTitle: 'Planda neler var?',
    compareColStarter: 'Starter',
    compareColPro: 'Pro',
    compareColEnterprise: 'Enterprise',
    faqEyebrow: '— SSS',
    faqTitle: 'Sık sorulan sorular.',
    finalTitle: 'Hâlâ kararsız mısınız?',
    finalSubtitle: '20 dakikalık bir danışma görüşmesi ayarlayın. Rezaro’yu restoranınız için canlı gösterelim.',
    finalCtaTrial: 'Şimdi ücretsiz deneyin',
    finalCtaDemo: 'Demo ayarlayın',
  },
}

export function PricingView({ lang = 'de' }: { lang?: Lang }) {
  const tt = T[lang]
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly')

  function priceFor(p: Plan): number {
    return billing === 'yearly' ? Math.round(p.monthly * 0.8) : p.monthly
  }

  function cell(value: string | boolean) {
    if (value === true) return <Check className="h-4 w-4 text-pub-accent mx-auto" strokeWidth={2.5} />
    if (value === false) return <X className="h-4 w-4 text-pub-rule mx-auto" strokeWidth={2.5} />
    return <span className="text-[12.5px] font-semibold text-pub-ink">{value}</span>
  }

  return (
    <div className="bg-pub-bg" style={{ fontFamily: 'var(--font-pub)' }}>
      <style>{`
        .display-serif { font-family: var(--font-display); font-style: italic; font-weight: 400; }
        .gradient-text {
          color: #fcd9a8; /* warm cream highlight that pops on the green hero */
          font-family: var(--font-display);
          font-weight: 500;
          font-style: italic;
          letter-spacing: -0.02em;
        }
        .mesh-bg {
          background-image:
            radial-gradient(at 18% 0%, rgba(245, 230, 211, 0.10) 0px, transparent 55%),
            radial-gradient(at 82% 0%, rgba(194, 65, 12, 0.10) 0px, transparent 55%);
        }
      `}</style>

      <PublicNav lang={lang} />

      {/* HERO */}
      <section className="relative overflow-hidden bg-pub-dark text-white">
        <div className="absolute inset-0 mesh-bg opacity-90" />
        <div className="relative mx-auto max-w-4xl px-6 pt-24 pb-16 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pub-accent mb-4">{tt.heroEyebrow}</p>
          <h1 className="font-semibold leading-[0.95] tracking-tight"
            style={{ fontSize: 'clamp(2.6rem, 5vw, 4.5rem)' }}>
            {tt.heroTitleLine1}<br />
            <span className="display-serif gradient-text">{tt.heroTitleLine2}</span>
          </h1>
          <p className="mt-7 max-w-xl mx-auto text-[16px] text-white/65 leading-relaxed">
            {tt.heroSubtitle}
          </p>

          {/* Billing toggle */}
          <div className="mt-10 inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 backdrop-blur p-1">
            <button onClick={() => setBilling('monthly')}
              className={`rounded-full px-5 py-2 text-[13px] font-semibold transition-colors ${billing === 'monthly' ? 'bg-white text-pub-dark' : 'text-white/70'}`}>
              {tt.billingMonthly}
            </button>
            <button onClick={() => setBilling('yearly')}
              className={`rounded-full px-5 py-2 text-[13px] font-semibold transition-colors flex items-center gap-2 ${billing === 'yearly' ? 'bg-white text-pub-dark' : 'text-white/70'}`}>
              {tt.billingYearly}
              <span className={`rounded-full px-2 py-0.5 text-[10px] ${billing === 'yearly' ? 'bg-emerald-500 text-white' : 'bg-emerald-500/20 text-emerald-400'}`}>
                −20 %
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* PLAN CARDS */}
      <section className="relative bg-pub-bg">
        <div className="mx-auto max-w-6xl px-6 pt-16 pb-20">
          <div className="grid gap-6 md:grid-cols-3 items-stretch">
            {tt.plans.map(p => {
              const price = priceFor(p)
              return (
                <div key={p.id}
                  className={`relative flex flex-col rounded-3xl p-7 ${p.recommended
                    ? 'border-2 border-pub-accent bg-pub-surface shadow-xl shadow-pub-accent/15 z-10'
                    : 'border border-pub-rule bg-pub-surface'}`}>
                  {p.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-pub-accent px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-white">
                      {p.badge}
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-2">
                    <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${p.recommended ? 'bg-pub-accent text-white' : 'bg-pub-accent-bg text-pub-accent'}`}>
                      <p.icon className="h-5 w-5" strokeWidth={1.6} />
                    </div>
                    <p className="text-[20px] font-semibold text-pub-ink">{p.name}</p>
                  </div>
                  <p className="text-[13px] text-pub-muted mb-6">{p.tagline}</p>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className="font-semibold text-pub-ink tracking-tight"
                      style={{ fontSize: '2.6rem', lineHeight: 1, fontFamily: 'var(--font-display)' }}>
                      {price} €
                    </span>
                    <span className="text-pub-muted text-sm">{tt.perMonth}</span>
                  </div>
                  <p className="text-[11px] text-pub-muted mb-6">
                    {billing === 'yearly'
                      ? tt.yearlyNote(price * 12, (p.monthly - price) * 12)
                      : tt.monthlyNote}
                  </p>
                  <ul className="space-y-2.5 mb-7 flex-1">
                    {p.highlights.map(h => (
                      <li key={h} className="flex items-start gap-2.5 text-[13px] text-pub-ink">
                        <Check className="h-3.5 w-3.5 text-pub-accent mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                        {h}
                      </li>
                    ))}
                  </ul>
                  <Link href={lp(`/signup?plan=${p.id}&billing=${billing}`, lang)}
                    className={`block w-full text-center rounded-full py-3 text-[13px] font-semibold transition-colors ${
                      p.recommended ? 'bg-pub-accent text-white hover:bg-pub-accent-dark' : 'border border-pub-ink text-pub-ink hover:bg-pub-ink hover:text-white'
                    }`}>
                    {tt.ctaStartTrial}
                  </Link>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ADD-ONS */}
      <section className="bg-pub-bg pb-20">
        <div className="mx-auto max-w-6xl px-6">
          <div className="rounded-3xl border border-pub-rule bg-pub-surface p-8 lg:p-12">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pub-accent mb-3">
                  {tt.addonsEyebrow}
                </p>
                <h2 className="font-semibold tracking-tight text-pub-ink leading-tight"
                  style={{ fontSize: 'clamp(1.6rem, 2.8vw, 2.2rem)' }}>
                  {tt.addonsTitlePre}<span className="display-serif" style={{ color: '#176a47' }}>{tt.addonsTitleEm}</span>{tt.addonsTitlePost}
                </h2>
              </div>
              <p className="text-[13px] text-pub-muted max-w-sm">
                {tt.addonsLead}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-pub-rule rounded-2xl overflow-hidden border border-pub-rule">
              {tt.addons.map(a => (
                <div key={a.id} className="bg-pub-surface p-6 flex flex-col">
                  <p className="text-[15px] font-semibold text-pub-ink mb-1">{a.name}</p>
                  <p className="text-[12.5px] text-pub-muted leading-relaxed mb-5 flex-1">{a.description}</p>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-[28px] font-semibold text-pub-ink leading-none"
                      style={{ fontFamily: 'var(--font-display)' }}>
                      {a.price}
                    </span>
                    <span className="text-[12px] text-pub-muted">{a.unit}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="border-y border-pub-rule bg-pub-surface">
        <div className="mx-auto max-w-6xl px-6 py-20">
          <div className="text-center mb-12">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pub-accent mb-3">{tt.compareEyebrow}</p>
            <h2 className="font-semibold tracking-tight text-pub-ink"
              style={{ fontSize: 'clamp(1.8rem, 3vw, 2.6rem)' }}>
              {tt.compareTitle}
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] border-separate border-spacing-0">
              <thead>
                <tr className="border-b-2 border-pub-rule">
                  <th scope="col" className="sticky left-0 z-10 bg-pub-bg text-left py-5 text-[12px] font-semibold uppercase tracking-widest text-pub-muted border-b-2 border-pub-rule"></th>
                  <th scope="col" className="text-center py-5 px-4 text-[14px] font-bold text-pub-ink border-b-2 border-pub-rule">{tt.compareColStarter}</th>
                  <th scope="col" className="text-center py-5 px-4 text-[14px] font-bold text-pub-accent border-b-2 border-pub-rule">{tt.compareColPro}</th>
                  <th scope="col" className="text-center py-5 px-4 text-[14px] font-bold text-pub-ink border-b-2 border-pub-rule">{tt.compareColEnterprise}</th>
                </tr>
              </thead>
              <tbody>
                {tt.compare.map(group => (
                  <Fragment key={group.section}>
                    <tr>
                      <td colSpan={4} className="sticky left-0 z-10 bg-pub-bg pt-7 pb-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-pub-accent">
                        {group.section}
                      </td>
                    </tr>
                    {group.features.map((f, i) => (
                      <tr key={f.name} className={i === 0 ? 'border-t-0' : ''}>
                        <th scope="row" className="sticky left-0 z-10 bg-pub-bg py-3 pr-4 text-[13.5px] font-normal text-left text-pub-ink border-b border-pub-rule/50">{f.name}</th>
                        <td className="py-3 px-4 text-center border-b border-pub-rule/50">{cell(f.s)}</td>
                        <td className="py-3 px-4 text-center bg-pub-accent-bg/30 border-b border-pub-rule/50">{cell(f.p)}</td>
                        <td className="py-3 px-4 text-center border-b border-pub-rule/50">{cell(f.e)}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-pub-bg">
        <div className="mx-auto max-w-3xl px-6 py-20">
          <div className="text-center mb-12">
            <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pub-accent mb-3">{tt.faqEyebrow}</p>
            <h2 className="font-semibold tracking-tight text-pub-ink"
              style={{ fontSize: 'clamp(1.8rem, 3vw, 2.6rem)' }}>
              {tt.faqTitle}
            </h2>
          </div>
          <div className="space-y-3">
            {tt.faqs.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-pub-rule bg-pub-surface p-6 transition-colors open:border-pub-accent">
                <summary className="flex cursor-pointer items-center justify-between gap-4 font-medium text-pub-ink text-[15px]">
                  {f.q}
                  <span className="text-pub-accent transition-transform group-open:rotate-45 text-3xl leading-none">+</span>
                </summary>
                <p className="mt-4 text-[14px] leading-relaxed text-pub-muted">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-pub-dark text-white">
        <div className="mx-auto max-w-3xl px-6 py-24 text-center">
          <h2 className="font-semibold tracking-tight"
            style={{ fontSize: 'clamp(2.2rem, 4vw, 3.4rem)' }}>
            {tt.finalTitle}
          </h2>
          <p className="mt-5 text-white/70 text-[16px]">
            {tt.finalSubtitle}
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link href={lp('/signup', lang)}
              className="inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[14px] font-semibold text-pub-dark hover:bg-white/90 transition-colors">
              {tt.finalCtaTrial} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link href="/service"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-[14px] font-semibold text-white hover:bg-white/10 transition-colors">
              {tt.finalCtaDemo}
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter lang={lang} />
    </div>
  )
}
